import { use, useEffect } from "react";
import { AuthContext, type IAuthContext } from "react-oauth2-code-pkce";
import { useTranslation } from "react-i18next";
import {
  Button,
  Card,
  Grid,
  Text,
  type GridFlow,
} from "@sede-enterprise/shell-ds-react-framework";
import { Container } from "../../components/SDS/Container";
import LogOutIcon from "../../assets/icons/LogOutIcon";
import "../../styles/App.css";

const Logout = () => {
  const { logOut, token, error }: IAuthContext = use(AuthContext);
  const { t } = useTranslation();

  const isCurrentlyLoggedIn = !!token;
  const hasAuthError = !!error;

  useEffect(() => {
    // Clear any OAuth parameters from URL when accessing logout page
    if (
      window.location.search.includes("code=") ||
      window.location.search.includes("state=")
    ) {
      window.history.replaceState({}, document.title, window.location.pathname);
    }

    // If user is currently logged in, perform logout
    if (isCurrentlyLoggedIn && !hasAuthError) {
      // Only clear session storage for this tab - no cross-tab synchronization
      sessionStorage.clear();
      logOut();
    }
  }, [logOut, isCurrentlyLoggedIn, hasAuthError]);

  const handleLoginAgain = () => {
    // Clear any residual auth data from session storage so the OAuth error
    // state (e.g. error=access_denied) held by the AuthProvider is not reused.
    sessionStorage.clear();

    // Hard-navigate to a clean home URL. This strips any lingering OAuth error
    // parameters (error / error_description) from the URL and fully resets the
    // in-memory AuthProvider state. Once reloaded on the clean URL, the app
    // automatically re-initiates the login flow.
    window.location.replace(window.location.origin);
  };

  const gridProps = {
    columns: 1,
    height: "auto",
    flow: "row" as GridFlow,
    className: "logout-grid",
  };

  return (
    <Container>
      <Grid {...gridProps}>
        <Grid.Cell>
          <Card className="logout-card">
            {/* Success Icon */}
            <div className="logout-success-icon">
              <svg width="36" height="36" fill="white" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                  clipRule="evenodd"
                />
              </svg>
            </div>

            {/* Title */}
            <Text size="large" className="logout-title">
              {hasAuthError
                ? t("auth.authErrorTitle", "Authentication Issue")
                : t("auth.logoutTitle", "You have been logged out")}
            </Text>

            {/* Description */}
            <Text size="medium" className="logout-description">
              {hasAuthError
                ? t(
                    "auth.authErrorSubtitle",
                    "We encountered an issue during authentication.",
                  )
                : t(
                    "auth.logoutSubtitle",
                    "You have been successfully logged out of this tab.",
                  )}
            </Text>

            {/* Action Section */}
            <Card className="logout-action-card">
              {hasAuthError ? (
                <Text
                  size="small"
                  className="auth-error-text"
                  style={{ color: "#d32f2f", marginBottom: "8px" }}
                >
                  {t(
                    "auth.authenticationFailed",
                    "Authentication failed. This can happen due to expired tokens or network issues.",
                  )}
                </Text>
              ) : null}

              <Text size="small" className="logout-action-text">
                {hasAuthError
                  ? t("auth.tryAgainMessage", "Please try signing in again.")
                  : t(
                      "auth.readyToContinue",
                      "Ready to continue? Sign in to access your account.",
                    )}
              </Text>

              <Button onClick={handleLoginAgain} className="logout-button">
                <LogOutIcon style={{ width: "16px", height: "16px" }} />
                {hasAuthError
                  ? t("auth.tryAgainButton", "Try Again")
                  : t("auth.signInButton", "Sign In Again")}
              </Button>
            </Card>
          </Card>
        </Grid.Cell>
      </Grid>
    </Container>
  );
};

export default Logout;
